import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { PMDialog } from "../../../utils/PMDialog";
import { EmployeePickerSheet } from "../../hours/hourFormPickers";
import { getUsersAsync } from "../../../../slices/user";
import { createRiskTiltak } from "../../../../api/hse/risk";
import { riskApiError } from "./riskUtils";
import { HseFieldInfoLabel } from "../HseFieldInfoLabel";

const inputClass =
  "w-full rounded-[14px] border-[1.5px] border-slate-200 bg-white px-4 py-3 text-[15px] text-slate-900 outline-none focus:border-[#1463C7]";

export function CreateRiskTiltakDialog({ open, onClose, risk, onCreated }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const users = useSelector((state) => state.users?.data || []);
  const [saving, setSaving] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [form, setForm] = useState({
    title: "",
    description: "",
    action_type: "generic",
    responsible_user_id: "",
    deadline: "",
  });

  useEffect(() => {
    if (!open) return;
    dispatch(getUsersAsync({ filters: [{ name: "EMPLOYEE", value: true }] }));
    setForm({
      title: risk?.title ? t("hse.risk.tiltak.title_from_risk", { title: risk.title }) : "",
      description: risk?.consequence || "",
      action_type: "generic",
      responsible_user_id: "",
      deadline: "",
    });
  }, [open, risk, dispatch, t]);

  const responsible = users.find((user) => String(user.id) === String(form.responsible_user_id));

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.title.trim()) {
      toast.error(t("hse.tiltak.error_title"));
      return;
    }
    if (!form.deadline) {
      toast.error(t("hse.tiltak.error_deadline"));
      return;
    }
    if (!form.responsible_user_id) {
      toast.error(t("hse.tiltak.error_assignee"));
      return;
    }
    try {
      setSaving(true);
      const created = await createRiskTiltak(risk.id, form);
      toast.success(t("hse.tiltak.created"));
      onCreated?.(created);
      onClose();
    } catch (error) {
      toast.error(riskApiError(error) || t("hse.tiltak.save_failed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <PMDialog
      open={open}
      onClose={onClose}
      title={t("hse.risk.tiltak.new")}
      size="lg"
      closeOnOutsideClick
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <label className="block">
          <HseFieldInfoLabel
            label={t("hse.tiltak.field.title")}
            infoTitle="hse.tiltak.info.title.title"
            infoBody="hse.tiltak.info.title.body"
          />
          <input
            className={`${inputClass} mt-2`}
            value={form.title}
            onChange={(event) => setForm((current) => ({ ...current, title: event.target.value }))}
          />
        </label>
        <label className="block">
          <span className="mb-2 block text-[13px] font-bold text-slate-700">
            {t("hse.tiltak.field.description")}
          </span>
          <textarea
            rows={3}
            className={inputClass}
            value={form.description}
            onChange={(event) =>
              setForm((current) => ({ ...current, description: event.target.value }))
            }
          />
        </label>
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block">
            <span className="mb-2 block text-[13px] font-bold text-slate-700">
              {t("hse.tiltak.field.deadline")}
            </span>
            <input
              type="date"
              className={inputClass}
              value={form.deadline}
              onChange={(event) =>
                setForm((current) => ({ ...current, deadline: event.target.value }))
              }
            />
          </label>
          <div>
            <span className="mb-2 block text-[13px] font-bold text-slate-700">
              {t("hse.tiltak.field.assignee")}
            </span>
            <button type="button" onClick={() => setPickerOpen(true)} className={`${inputClass} text-left`}>
              {responsible
                ? responsible.displayTitle || responsible.name
                : t("hse.risk.tiltak.choose_assignee")}
            </button>
            <EmployeePickerSheet
              open={pickerOpen}
              onClose={() => setPickerOpen(false)}
              selectedId={form.responsible_user_id}
              onSelect={(user) =>
                setForm((current) => ({ ...current, responsible_user_id: user?.id || "" }))
              }
            />
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700"
          >
            {t("hse.risk.cancel")}
          </button>
          <button
            type="submit"
            disabled={saving}
            className="rounded-full bg-[#1463C7] px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
          >
            {saving ? t("hse.risk.saving") : t("hse.risk.tiltak.create")}
          </button>
        </div>
      </form>
    </PMDialog>
  );
}
